import { SettingsWrapper } from '../index'
import {
  AddButtonHeaderCell,
  DeleteButtonCell,
  NumberInput,
  SelectDropdown,
  Switch,
  TableBody,
  TableCell,
  TableContainer,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from '@expane/ui'
import { Controller, SubmitHandler, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { ApplyButton } from 'widgets/Buttons'
import {
  useFetchBusinessRefundSettings,
  useUpdateBusinessRefundSettingsById,
} from '@expane/data/src/gql/businessRefundSettings'
import { FC, useState } from 'react'
import { ServerBusinessRefundSettingsType, ServerSaleEntityTypeEnum } from '@expane/data'
import { useSnackbar } from '@expane/widgets'
import { useTranslation } from 'react-i18next'
import { HorizontalTabs } from 'ui/HorizontalTabs'
import {
  convertRefundFormToServer,
  getRefundFormInputs,
  RefundForm,
  RefundsFromClientsSettingsForm,
  sortRefundFormInputs,
  units,
} from './logic'
import { translateData } from 'logic/utils'

export const RefundsFromClientsSettings = () => {
  const { data: businessRefundSettings } = useFetchBusinessRefundSettings()

  if (!businessRefundSettings) return null

  return <RefundsFromClientsSettingsLogic businessRefundSettings={businessRefundSettings} />
}

const RefundsFromClientsSettingsLogic: FC<{
  businessRefundSettings: ServerBusinessRefundSettingsType
}> = ({ businessRefundSettings }) => {
  const {
    refundBooking,
    refundGiftCard,
    refundSubscription,
    isRefundSubscriptionAllowed,
    isRefundGiftCardAllowed,
    isRefundBookingAllowed,
  } = getRefundFormInputs(businessRefundSettings)

  const [activeTabId, setActiveTabId] = useState(TAB_IDS.booking)

  const {
    control,
    handleSubmit,
    formState: { isDirty, isSubmitting },
    reset,
  } = useForm<RefundsFromClientsSettingsForm>({
    defaultValues: {
      isRefundSubscriptionAllowed,
      refundSubscription,
      isRefundBookingAllowed,
      refundBooking,
      isRefundGiftCardAllowed,
      refundGiftCard,
    },
  })

  const [openSnackBar] = useSnackbar()

  const { mutateAsync: updateBusinessRefundSettingsById } = useUpdateBusinessRefundSettingsById()

  const { t } = useTranslation()

  const {
    fields: subscriptionFields,
    remove: removeSubscriptionField,
    append: appendSubscriptionField,
    replace: replaceSubscriptionFields,
  } = useFieldArray({ control, name: 'refundSubscription' })

  const watchedSubscriptionFields = useWatch({
    control,
    name: 'refundSubscription',
  })

  const watchedIsRefundSubscriptionAllowed = useWatch({
    control,
    name: 'isRefundSubscriptionAllowed',
  })

  const {
    fields: giftCardFields,
    remove: removeGiftCardField,
    append: appendGiftCardField,
    replace: replaceGiftCardFields,
  } = useFieldArray({ control, name: 'refundGiftCard' })

  const watchedGiftCardFields = useWatch({
    control,
    name: 'refundGiftCard',
  })

  const watchedIsRefundGiftCardAllowed = useWatch({
    control,
    name: 'isRefundGiftCardAllowed',
  })

  const {
    fields: bookingFields,
    remove: removeBookingField,
    append: appendBookingField,
    replace: replaceBookingFields,
  } = useFieldArray({ control, name: 'refundBooking' })

  const watchedBookingFields = useWatch({
    control,
    name: 'refundBooking',
  })

  const watchedIsRefundBookingAllowed = useWatch({
    control,
    name: 'isRefundBookingAllowed',
  })

  const handleUpdatingBusiness: SubmitHandler<RefundsFromClientsSettingsForm> = async ({
    isRefundBookingAllowed,
    refundBooking,
    isRefundGiftCardAllowed,
    refundGiftCard,
    isRefundSubscriptionAllowed,
    refundSubscription,
  }) => {
    const subscriptionPolicyId = businessRefundSettings.refundSettingsPolicies.find(
      police => police.type === ServerSaleEntityTypeEnum.Subscription,
    )?.id

    const bookingPolicyId = businessRefundSettings.refundSettingsPolicies.find(
      police => police.type === ServerSaleEntityTypeEnum.Booking,
    )?.id

    const giftCardPolicyId = businessRefundSettings.refundSettingsPolicies.find(
      police => police.type === ServerSaleEntityTypeEnum.GiftCard,
    )?.id

    if (subscriptionPolicyId && bookingPolicyId && giftCardPolicyId) {
      const businessRefundSettingsDto = {
        isActive: true,
        refundSettingId: businessRefundSettings.id,
        isRefundSubscriptionAllowed,
        isRefundGiftCardAllowed,
        isRefundBookingAllowed,
        businessRefundSettingsThresholdsInsertInput: [
          ...convertRefundFormToServer(refundSubscription, subscriptionPolicyId),
          ...convertRefundFormToServer(refundBooking, bookingPolicyId),
          ...convertRefundFormToServer(refundGiftCard, giftCardPolicyId),
        ],
        businessRefundSettingsPolicyIdsToDeleteThresholds: [
          subscriptionPolicyId,
          bookingPolicyId,
          giftCardPolicyId,
        ],
      }

      const result = await updateBusinessRefundSettingsById(businessRefundSettingsDto)

      if (
        result?.insertBusinessRefundSettingsThresholds?.affected_rows &&
        result?.deleteBusinessRefundSettingsThresholds?.affected_rows
      ) {
        openSnackBar(t('changesSaved'), 'success', 3000)

        // Обновляем значения формы
        reset({
          isRefundSubscriptionAllowed,
          refundSubscription,
          isRefundBookingAllowed,
          refundBooking,
          isRefundGiftCardAllowed,
          refundGiftCard,
        })
      } else openSnackBar(t('submitError'), 'error', 3000)
    }
  }

  return (
    <SettingsWrapper>
      <div className={'flex items-center justify-between gap-4 mb-4'}>
        <ApplyButton
          className="w-fit"
          disabled={!isDirty || isSubmitting}
          spinner={isSubmitting}
          onClick={handleSubmit(handleUpdatingBusiness)}
        />

        <div className={'flex-1'}>
          <HorizontalTabs
            tabs={tabs.map(tab => ({ ...tab, label: t(tab.label) }))}
            activeTabId={activeTabId}
            setActiveTabId={async id => {
              const tabFieldMap = {
                [TAB_IDS.booking]: watchedBookingFields,
                [TAB_IDS.giftCards]: watchedGiftCardFields,
                [TAB_IDS.subscriptions]: watchedSubscriptionFields,
              }

              const fieldsToCheck = tabFieldMap[activeTabId]
              // formState.errors - здесь работает не правильно
              if (fieldsToCheck && hasEmptyFields(fieldsToCheck)) {
                await handleSubmit(handleUpdatingBusiness)()
                return
              }

              setActiveTabId(id)
            }}
            title={''}
          />
        </div>
      </div>

      {activeTabId === TAB_IDS.booking ? (
        <>
          <Controller
            control={control}
            name="isRefundBookingAllowed"
            render={({ field: { onChange, value } }) => (
              <Switch
                onChange={onChange}
                checked={value}
                label={t('refundsFromClients.allowRefundBookings')}
                containerClassName={'mb-4'}
              />
            )}
          />

          <TableContainer>
            <RefundSettingsTableHeader
              onClick={() =>
                appendBookingField({
                  unitId: undefined,
                  unitValue: '',
                  percentage: '',
                })
              }
              disabled={!isRefundBookingAllowed}
              periodTitle={t('refundsFromClients.periodToDateOfBooking')}
            />

            <TableBody>
              {bookingFields.map((item, index) => {
                return (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Controller
                        control={control}
                        rules={{ required: watchedIsRefundBookingAllowed }}
                        name={`refundBooking.${index}.unitValue` as 'refundBooking.0.unitValue'}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <NumberInput
                            value={value}
                            onChange={onChange}
                            errorMessage={{
                              isShown: Boolean(error),
                              text: ' ',
                              reserveIndent: false,
                            }}
                            height="small"
                            className={'w-40'}
                            allowDecimals={false}
                            disabled={!watchedIsRefundBookingAllowed}
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <Controller
                        control={control}
                        rules={{ required: watchedIsRefundBookingAllowed }}
                        name={`refundBooking.${index}.unitId` as 'refundBooking.0.unitId'}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <SelectDropdown
                            value={value}
                            onSelectChange={id => {
                              onChange(id)

                              // так как на момент onChange watchedBookingFields будет иметь старое значение
                              // сортировка будет работать не правильно
                              const newWatchedBookingFields = watchedBookingFields.map((item, i) =>
                                i === index && id ? { ...item, unitId: id } : item,
                              )

                              sortAndUpdateRefundFields(
                                newWatchedBookingFields,
                                replaceBookingFields,
                              )
                            }}
                            items={translateData(units, t)}
                            errorMessage={{
                              isShown: Boolean(error),
                              text: ' ',
                              reserveIndent: false,
                            }}
                            height="small"
                            className={'w-40'}
                            disabled={!watchedIsRefundBookingAllowed}
                          />
                        )}
                      />
                    </TableCell>

                    <TableCell>
                      <Controller
                        control={control}
                        rules={{ required: watchedIsRefundBookingAllowed }}
                        name={`refundBooking.${index}.percentage` as 'refundBooking.0.percentage'}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <NumberInput
                            value={value}
                            onChange={onChange}
                            errorMessage={{
                              isShown: Boolean(error),
                              text: ' ',
                              reserveIndent: false,
                            }}
                            height="small"
                            className={'w-40'}
                            allowDecimals={false}
                            disabled={!watchedIsRefundBookingAllowed}
                            onBlur={() =>
                              sortAndUpdateRefundFields(watchedBookingFields, replaceBookingFields)
                            }
                          />
                        )}
                      />
                    </TableCell>

                    {index !== 0 && <DeleteButtonCell onClick={() => removeBookingField(index)} />}
                  </TableRow>
                )
              })}
            </TableBody>
          </TableContainer>
        </>
      ) : null}

      {activeTabId === TAB_IDS.subscriptions ? (
        <>
          <Controller
            control={control}
            name="isRefundSubscriptionAllowed"
            render={({ field: { onChange, value } }) => (
              <Switch
                onChange={onChange}
                checked={value}
                label={t('refundsFromClients.allowRefundSubscriptions')}
                containerClassName={'my-4'}
              />
            )}
          />

          <TableContainer>
            <RefundSettingsTableHeader
              onClick={() =>
                appendSubscriptionField({
                  unitId: undefined,
                  unitValue: '',
                  percentage: '',
                })
              }
              disabled={!isRefundSubscriptionAllowed}
              periodTitle={t('refundsFromClients.periodFromDateOfPurchase')}
            />
            <TableBody>
              {subscriptionFields.map((item, index) => {
                return (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Controller
                        control={control}
                        rules={{ required: watchedIsRefundSubscriptionAllowed }}
                        name={
                          `refundSubscription.${index}.unitValue` as 'refundSubscription.0.unitValue'
                        }
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <NumberInput
                            value={value}
                            onChange={onChange}
                            errorMessage={{
                              isShown: Boolean(error),
                              text: ' ',
                              reserveIndent: false,
                            }}
                            height="small"
                            className={'w-40'}
                            allowDecimals={false}
                            disabled={!watchedIsRefundSubscriptionAllowed}
                          />
                        )}
                      />
                    </TableCell>

                    <TableCell>
                      <Controller
                        control={control}
                        rules={{ required: watchedIsRefundSubscriptionAllowed }}
                        name={`refundSubscription.${index}.unitId` as 'refundSubscription.0.unitId'}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <SelectDropdown
                            value={value}
                            onSelectChange={id => {
                              onChange(id)

                              // так как на момент onChange watchedSubscriptionFields будет иметь старое значение
                              // сортировка будет работать не правильно
                              const newWatchedSubscriptionFields = watchedSubscriptionFields.map(
                                (item, i) => (i === index && id ? { ...item, unitId: id } : item),
                              )

                              sortAndUpdateRefundFields(
                                newWatchedSubscriptionFields,
                                replaceSubscriptionFields,
                              )
                            }}
                            items={translateData(units, t)}
                            errorMessage={{
                              isShown: Boolean(error),
                              text: ' ',
                              reserveIndent: false,
                            }}
                            height="small"
                            className={'w-40'}
                            disabled={!watchedIsRefundSubscriptionAllowed}
                          />
                        )}
                      />
                    </TableCell>

                    <TableCell>
                      <Controller
                        control={control}
                        rules={{ required: watchedIsRefundSubscriptionAllowed }}
                        name={
                          `refundSubscription.${index}.percentage` as 'refundSubscription.0.percentage'
                        }
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <NumberInput
                            value={value}
                            onChange={onChange}
                            errorMessage={{
                              isShown: Boolean(error),
                              text: ' ',
                              reserveIndent: false,
                            }}
                            height="small"
                            className={'w-40'}
                            allowDecimals={false}
                            disabled={!watchedIsRefundSubscriptionAllowed}
                            onBlur={() =>
                              sortAndUpdateRefundFields(
                                watchedSubscriptionFields,
                                replaceSubscriptionFields,
                              )
                            }
                          />
                        )}
                      />
                    </TableCell>

                    {index !== 0 && (
                      <DeleteButtonCell onClick={() => removeSubscriptionField(index)} />
                    )}
                  </TableRow>
                )
              })}
            </TableBody>
          </TableContainer>
        </>
      ) : null}

      {activeTabId === TAB_IDS.giftCards ? (
        <>
          <Controller
            control={control}
            name="isRefundGiftCardAllowed"
            render={({ field: { onChange, value } }) => (
              <Switch
                onChange={onChange}
                checked={value}
                label={t('refundsFromClients.allowRefundGiftCards')}
                containerClassName={'my-4'}
              />
            )}
          />

          <TableContainer>
            <RefundSettingsTableHeader
              onClick={() =>
                appendGiftCardField({
                  unitId: undefined,
                  unitValue: '',
                  percentage: '',
                })
              }
              disabled={!isRefundGiftCardAllowed}
              periodTitle={t('refundsFromClients.periodFromDateOfPurchase')}
            />
            <TableBody>
              {giftCardFields.map((item, index) => {
                return (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Controller
                        control={control}
                        rules={{ required: watchedIsRefundGiftCardAllowed }}
                        name={`refundGiftCard.${index}.unitValue` as 'refundGiftCard.0.unitValue'}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <NumberInput
                            value={value}
                            onChange={onChange}
                            errorMessage={{
                              isShown: Boolean(error),
                              text: ' ',
                              reserveIndent: false,
                            }}
                            height="small"
                            className={'w-40'}
                            allowDecimals={false}
                            disabled={!watchedIsRefundGiftCardAllowed}
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <Controller
                        control={control}
                        rules={{ required: watchedIsRefundGiftCardAllowed }}
                        name={`refundGiftCard.${index}.unitId` as 'refundGiftCard.0.unitId'}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <SelectDropdown
                            value={value}
                            onSelectChange={id => {
                              onChange(id)

                              // так как на момент onChange watchedGiftCardFields будет иметь старое значение
                              // сортировка будет работать не правильно
                              const newWatchedGiftCardFields = watchedGiftCardFields.map(
                                (item, i) => (i === index && id ? { ...item, unitId: id } : item),
                              )

                              sortAndUpdateRefundFields(
                                newWatchedGiftCardFields,
                                replaceGiftCardFields,
                              )
                            }}
                            items={translateData(units, t)}
                            errorMessage={{
                              isShown: Boolean(error),
                              text: ' ',
                              reserveIndent: false,
                            }}
                            height="small"
                            className={'w-40'}
                            disabled={!watchedIsRefundGiftCardAllowed}
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <Controller
                        control={control}
                        rules={{ required: watchedIsRefundGiftCardAllowed }}
                        name={`refundGiftCard.${index}.percentage` as 'refundGiftCard.0.percentage'}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <NumberInput
                            value={value}
                            onChange={onChange}
                            errorMessage={{
                              isShown: Boolean(error),
                              text: ' ',
                              reserveIndent: false,
                            }}
                            height="small"
                            className={'w-40'}
                            allowDecimals={false}
                            disabled={!watchedIsRefundGiftCardAllowed}
                            onBlur={() =>
                              sortAndUpdateRefundFields(
                                watchedGiftCardFields,
                                replaceGiftCardFields,
                              )
                            }
                          />
                        )}
                      />
                    </TableCell>

                    {index !== 0 && <DeleteButtonCell onClick={() => removeGiftCardField(index)} />}
                  </TableRow>
                )
              })}
            </TableBody>
          </TableContainer>
        </>
      ) : null}
    </SettingsWrapper>
  )
}

const RefundSettingsTableHeader: FC<{
  periodTitle: string
  onClick: () => void
  disabled: boolean
}> = ({ periodTitle, onClick, disabled }) => {
  const { t } = useTranslation()

  return (
    <TableHeader>
      <tr>
        <TableHeaderCell colSpan={2}>{periodTitle}</TableHeaderCell>
        <TableHeaderCell className="w-40">{t('refundsFromClients.percentage')}</TableHeaderCell>
        <AddButtonHeaderCell onClick={onClick} disabled={disabled} />
      </tr>
    </TableHeader>
  )
}

const hasEmptyFields = (fields: RefundForm[]) =>
  fields.some(field => !field.unitId || !field.unitValue || !field.percentage)

const TAB_IDS = {
  booking: 1,
  subscriptions: 2,
  giftCards: 3,
}

const tabs = [
  {
    id: TAB_IDS.booking,
    label: 'bookings.title',
  },
  {
    id: TAB_IDS.subscriptions,
    label: 'subscriptions',
  },
  { id: TAB_IDS.giftCards, label: 'giftCards' },
]

const sortAndUpdateRefundFields = (
  fields: RefundForm[],
  replaceFn: (data: RefundForm[]) => void,
) => {
  // если не введены данные периода, то сортировка не происходит
  if (fields.some(field => field.unitId === undefined || field.unitValue === '')) return

  const sorted = sortRefundFormInputs(fields)

  replaceFn(sorted)
}
